/**
 * apiFetch — wrapper around fetch() that transparently handles the P1-B
 * refresh-cookie pattern (Phase 1).
 *
 * Behaviour:
 *  1. Attach `Authorization: Bearer <access_token>` if we have one.
 *  2. Always send credentials (so the HttpOnly refresh cookie rides along
 *     on `POST /api/auth/refresh`).
 *  3. On 401 → call `/api/auth/refresh` **once** (single-flight) to swap the
 *     sealed envelope for a fresh 15-min access token. Retry the original
 *     request with the new token. If refresh also fails → surface the 401
 *     and let the caller redirect to login.
 *
 * Concurrency guarantee: if 20 parallel requests all hit 401, only ONE
 * refresh call fires; the other 19 await the same in-flight promise.
 */
import { getToken, setToken, clearToken } from './tokenManager';
import logger from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Single-flight state: the in-flight refresh promise (or null). */
let inflightRefresh = null;

async function _callRefresh() {
  const r = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
  });
  if (!r.ok) throw new Error(`refresh_failed_${r.status}`);
  const body = await r.json();
  if (!body.token) throw new Error('refresh_no_token');
  setToken(body.token);
  return body.token;
}

/** Public entry: returns a fresh token if refresh worked, otherwise clears the token and throws. */
export async function refreshAccessToken() {
  if (!inflightRefresh) {
    inflightRefresh = _callRefresh().finally(() => { inflightRefresh = null; });
  }
  try {
    return await inflightRefresh;
  } catch (err) {
    clearToken();
    throw err;
  }
}

/** Drop-in fetch replacement. Absolute URLs pass through untouched. */
export async function apiFetch(input, init = {}) {
  const isAbsolute = typeof input === 'string' && /^https?:\/\//i.test(input);
  const url = isAbsolute ? input : `${BACKEND_URL}${input}`;
  const withAuth = _buildInit(init, getToken());
  const resp = await fetch(url, withAuth);
  if (resp.status !== 401) return resp;
  // 401 — try once to refresh + retry the request. If refresh also fails, return the original 401.
  try {
    const freshToken = await refreshAccessToken();
    return fetch(url, _buildInit(init, freshToken));
  } catch (err) {
    logger.warn('apiFetch: refresh failed, surfacing 401', err?.message);
    return resp;
  }
}

function _buildInit(init, token) {
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  return { ...init, headers, credentials: init.credentials || 'include' };
}


/* ------------------------------------------------------------------ */
/*  Global fetch interceptor (P1-B Phase 2)                            */
/*                                                                     */
/*  Patches ``window.fetch`` so every existing call site gets the      */
/*  refresh-on-401 behaviour without touching 134 files. Only touches  */
/*  requests aimed at REACT_APP_BACKEND_URL — external URLs pass       */
/*  through untouched.                                                 */
/* ------------------------------------------------------------------ */

let _installed = false;

export function installGlobalFetchInterceptor() {
  if (_installed || typeof window === 'undefined' || !window.fetch) return;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (!_isBackendUrl(url)) return nativeFetch(input, init);
    return _authenticatedFetch(nativeFetch, url, init);
  };
  _installed = true;
}

function _isBackendUrl(url) {
  if (!BACKEND_URL) return false;
  return url.startsWith(BACKEND_URL) || url.startsWith('/api/');
}

async function _authenticatedFetch(nativeFetch, url, init) {
  const initialToken = getToken();
  const withAuth = _buildInit(init, initialToken);
  const resp = await nativeFetch(url, withAuth);
  // Never try to refresh the refresh call itself — that would loop forever.
  if (resp.status !== 401 || url.includes('/api/auth/refresh')) return resp;
  // If there was no local token to start with (e.g. an anonymous landing
  // page rendering that happens to fire an /api/* call), there is nothing
  // to refresh. Return the 401 quietly — no warning, no retry.
  if (!initialToken) return resp;
  try {
    const freshToken = await refreshAccessToken();
    return nativeFetch(url, _buildInit(init, freshToken));
  } catch (err) {
    logger.warn('global fetch: refresh failed, surfacing 401');
    return resp;
  }
}
